import { isAbortError } from '@lib/ai/utils/is-abort-error';
import { ApiError } from '@lib/api/api-response';
import { DOCUMENT_UPLOAD_SENTRY_REPORTED } from '@lib/observability/document-upload/sentry-reported-marker';
import { isBrowserFetchTransportError } from '@lib/shared/utils/is-browser-fetch-transport-error';

import { captureException } from './capture-exception.client';
import { shouldSuppressEntitlementDeniedError } from './should-suppress-entitlement-denied-error';
import { shouldSuppressSentryForMeta } from './should-suppress-sentry-for-meta';
import { shouldSuppressSignedOutQueryError } from './should-suppress-signed-out-query-error';

type TanstackErrorSource = 'tanstack_query' | 'tanstack_mutation';

interface TanstackErrorContext {
  queryKey?: readonly unknown[];
  mutationKey?: readonly unknown[];
  variables?: unknown;
  meta?: Record<string, unknown>;
  fetchStatus?: string;
  failureCount?: number;
}

/**
 * Captures a TanStack Query/Mutation error to Sentry (and PostHog via mirror).
 *
 * Wired into the global `QueryCache` and `MutationCache` `onError` handlers in
 * `query-client.ts`, so every failed query/mutation is logged automatically —
 * call sites do not need their own `onError` to report errors.
 *
 * Enriches the event with the `queryKey`/`mutationKey`, mutation `variables`,
 * `meta`, retry/fetch state, and (when the error is an `ApiError`) the HTTP
 * status, so failures can be filtered in Sentry without inspecting the stack.
 *
 * Mutation `variables` are deep-scrubbed for sensitive keys (password, secret,
 * token, etc.) before being sent — auth mutations carry raw passwords and we
 * never want those in Sentry.
 *
 * Call sites may opt out of Sentry reporting for known-expected failures via
 * `useQuery({ meta: { suppressSentryStatuses: [401, 403] } })` (or the
 * mutation equivalent). Errors are only suppressed when they are `ApiError`s
 * whose `.status` appears in that list — non-API errors are always reported.
 *
 * User-facing errors handled at the call site (e.g. SSE stream error frames
 * shown via toast) may opt out via `meta.suppressSentryErrorNames`.
 *
 * `AbortError` / `ResponseAborted` are always skipped: they mean the request
 * was intentionally cancelled (superseding autosave, unmount, navigation) and
 * are not actionable production faults.
 *
 * Browser fetch transport failures are also suppressed after TanStack retries
 * — raw `TypeError: Failed to fetch` / `network error` / `Load failed` /
 * Firefox `NetworkError…`, and `apiFetch`-wrapped `ApiError(..., 0)` with the
 * same messages. Without an HTTP response or server stack these are
 * connectivity/client lifecycle noise (PORTAL-WY / PORTAL-99 / PORTAL-WV /
 * issue 7693570517). Server `ApiError`s with real HTTP statuses remain
 * reportable.
 */
export function captureTanstackError(
  error: Error,
  source: TanstackErrorSource,
  context: TanstackErrorContext
): void {
  if (isAbortError(error)) return;
  // Raw TypeErrors have no HTTP status, and apiFetch wraps them as status-0
  // ApiErrors — `suppressSentryStatuses` cannot match either shape reliably
  // without per-hook meta. Drop them here for queries and mutations.
  if (isBrowserFetchTransportError(error)) return;
  if (shouldSuppressSentryForMeta(error, context.meta)) return;
  if (shouldSuppressSignedOutQueryError(error, context.meta)) return;
  if (shouldSuppressEntitlementDeniedError(error)) return;
  if (
    (error as unknown as Record<symbol, unknown>)[
      DOCUMENT_UPLOAD_SENTRY_REPORTED
    ] === true
  ) {
    // Already reported by the upload hook with per-upload tags/context, in a
    // forked Sentry scope. Skip here to avoid duplicate Sentry events.
    return;
  }

  const apiTags = error instanceof ApiError ? { apiStatus: error.status } : {};

  captureException(error, {
    exception_source: source,
    ...context,
    variables: redactSensitive(context.variables),
    ...apiTags,
  });
}

const REDACTED = '[REDACTED]';
const SENSITIVE_KEY_PATTERN =
  /(password|secret|token|apiKey|api_key|authorization|credential)/i;

function redactSensitive(value: unknown): unknown {
  if (value instanceof File) {
    return {
      name: value.name,
      sizeBytes: value.size,
      mimeType: value.type || 'application/octet-stream',
    };
  }
  if (Array.isArray(value)) {
    return value.map(redactSensitive);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).map(
      ([key, val]) => [
        key,
        SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : redactSensitive(val),
      ]
    );
    return Object.fromEntries(entries);
  }
  return value;
}
